import { useEffect } from 'react';

const SITE_URL = 'https://unbeatableloans.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo-bg.png`;

function setMetaTag(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Sets document title/description plus the per-page canonical link and
 * Open Graph / Twitter Card tags every marketing page needs — without this,
 * every route inherits index.html's single static homepage OG tags, so a
 * shared blog post link previews as the homepage everywhere.
 */
export function usePageMeta(
  title: string,
  description?: string,
  options: { image?: string } = {}
) {
  useEffect(() => {
    const prevTitle = document.title;
    const base = 'Unbeatable Loans';
    const fullTitle = title.includes('Unbeatable')
      ? title
      : `${title} | ${base}`;
    document.title = fullTitle;
    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('name', 'twitter:title', fullTitle);

    if (description) {
      setMetaTag('name', 'description', description);
      setMetaTag('property', 'og:description', description);
      setMetaTag('name', 'twitter:description', description);
    }

    const canonicalUrl = `${SITE_URL}${window.location.pathname}`;
    let canonicalLink = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    setMetaTag('property', 'og:url', canonicalUrl);

    const image = options.image ?? DEFAULT_OG_IMAGE;
    setMetaTag('property', 'og:image', image);
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:image', image);

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, options.image]);
}
